import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, User as UserIcon, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: user?.username || '',
    email: user?.email || '',
    name: user?.name || '',
    password: '',
    confirmPassword: '',
    email_notifications_enabled: user?.email_notifications_enabled || false,
    notification_mode: user?.notification_mode || 'immediate',
    digest_time: user?.digest_time || '18:00',
    digest_timezone: user?.digest_timezone || 'Europe/Berlin'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    setIsLoading(true);
    try {
      const updateData = {
        username: formData.username,
        email: formData.email,
        name: formData.name,
        email_notifications_enabled: formData.email_notifications_enabled,
        notification_mode: formData.notification_mode,
        digest_time: formData.digest_time,
        digest_timezone: formData.digest_timezone
      };
      
      if (formData.password) {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/auth/profile`, updateData, {
        headers: getAuthHeader()
      });
      
      toast.success('Profil erfolgreich aktualisiert!');
      setFormData({ ...formData, password: '', confirmPassword: '' });
      
      // Reload user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren des Profils');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                data-testid="back-button"
                onClick={() => navigate('/')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Zurück</span>
              </Button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                  <UserIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">Mein Profil</h1>
                  <p className="text-sm text-gray-500">Verwalten Sie Ihre persönlichen Daten</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Read-only fields */}
            <div className="pb-6 border-b">
              <div>
                <Label>Rolle</Label>
                <Input
                  value={user?.role === 'admin' ? 'Administrator' : 'Kunde'}
                  disabled
                  className="mt-2 bg-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">Rolle kann nur von Admins geändert werden</p>
              </div>
            </div>

            {/* Editable fields */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800">Persönliche Informationen</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="username">Benutzername</Label>
                  <Input
                    id="username"
                    data-testid="profile-username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="name">Vollständiger Name</Label>
                  <Input
                    id="name"
                    data-testid="profile-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ihr Name"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="profile-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="mt-2"
                  />
                </div>
              </div>
            </div>

            {/* Password change */}
            <div className="space-y-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800">Passwort ändern</h3>
              <p className="text-sm text-gray-500">Lassen Sie die Felder leer, wenn Sie das Passwort nicht ändern möchten</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="password">Neues Passwort</Label>
                  <Input
                    id="password"
                    type="password"
                    data-testid="profile-password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    data-testid="profile-confirm-password-input"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="••••••••"
                    className="mt-2"
                  />
                </div>
              </div>
            </div>

            {/* Email Notification Settings */}
            <div className="space-y-6 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800">📧 E-Mail-Benachrichtigungen</h3>
              <p className="text-sm text-gray-500">Erhalten Sie Updates zu Ihren Aufgaben per E-Mail</p>
              
              <div className="space-y-4">
                {/* Enable notifications toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <Label htmlFor="email_notifications" className="font-medium">E-Mail-Benachrichtigungen aktivieren</Label>
                    <p className="text-xs text-gray-500 mt-1">Benachrichtigungen bei Aufgabenänderungen erhalten</p>
                  </div>
                  <input
                    id="email_notifications"
                    type="checkbox"
                    checked={formData.email_notifications_enabled}
                    onChange={(e) => setFormData({ ...formData, email_notifications_enabled: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {formData.email_notifications_enabled && (
                  <>
                    {/* Notification mode */}
                    <div className="space-y-2">
                      <Label>Benachrichtigungsmodus</Label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, notification_mode: 'immediate' })}
                          className={`p-4 border-2 rounded-lg text-left transition-all ${
                            formData.notification_mode === 'immediate'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">⚡ Sofort</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Jede Änderung einzeln per E-Mail
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, notification_mode: 'digest' })}
                          className={`p-4 border-2 rounded-lg text-left transition-all ${
                            formData.notification_mode === 'digest'
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium">📊 Zusammenfassung</div>
                          <div className="text-xs text-gray-500 mt-1">
                            Gesammelte Änderungen zu festem Zeitpunkt
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Digest time (only if digest mode) */}
                    {formData.notification_mode === 'digest' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
                        <div>
                          <Label htmlFor="digest_time">Uhrzeit für Zusammenfassung</Label>
                          <Input
                            id="digest_time"
                            type="time"
                            value={formData.digest_time}
                            onChange={(e) => setFormData({ ...formData, digest_time: e.target.value })}
                            className="mt-2"
                          />
                          <p className="text-xs text-gray-500 mt-1">Wann möchten Sie die tägliche Zusammenfassung erhalten?</p>
                        </div>
                        <div>
                          <Label htmlFor="digest_timezone">Zeitzone</Label>
                          <select
                            id="digest_timezone"
                            value={formData.digest_timezone}
                            onChange={(e) => setFormData({ ...formData, digest_timezone: e.target.value })}
                            className="mt-2 w-full h-10 px-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="Europe/Berlin">Europa/Berlin (MEZ)</option>
                            <option value="Europe/Zurich">Europa/Zürich</option>
                            <option value="Europe/Vienna">Europa/Wien</option>
                            <option value="UTC">UTC</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-6 border-t">
              <Button
                type="submit"
                data-testid="save-profile-button"
                disabled={isLoading}
                className="bg-[#0073ea] hover:bg-[#0060c0] text-white flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>{isLoading ? 'Speichern...' : 'Änderungen speichern'}</span>
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default Profile;
